/**
 * Thin typed wrapper around fetch for calling the API Worker.
 *
 * Centralizing this now means every future feature calls `apiFetch`
 * instead of hand-rolling fetch + base URL + credentials + error parsing
 * in each component. Auth headers/cookies get added here in Step 3
 * without touching call sites.
 */

/**
 * Resolve the API Worker's origin.
 *
 * The API is a separate Cloudflare Worker, NOT part of the Pages site. If a
 * request like POST /api/auth/signup is sent to the Pages origin instead,
 * Pages answers 405 (static hosting only accepts GET/HEAD). That happens
 * when VITE_API_BASE_URL is empty or set to the Pages URL at build time, so
 * both cases are rejected loudly here instead of failing as a vague 405.
 *
 * `??` is not enough: an empty string ("VITE_API_BASE_URL=") is not nullish
 * and would silently produce same-origin requests.
 */
function resolveApiBaseUrl(): string {
  const configured = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
  if (configured) {
    const base = configured.replace(/\/+$/, "");
    if (typeof window !== "undefined" && base === window.location.origin && !import.meta.env.DEV) {
      console.error(
        `VITE_API_BASE_URL (${base}) points at the web app itself. It must be the API Worker URL, e.g. https://platform-api-production.<subdomain>.workers.dev`
      );
    }
    return base;
  }
  if (import.meta.env.DEV) return "http://127.0.0.1:8787";
  console.error(
    "VITE_API_BASE_URL is not set for this build. Set it to the API Worker URL and rebuild (Vite bakes it in at build time)."
  );
  return "";
}

const API_BASE_URL = resolveApiBaseUrl();

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    requestId: string;
  };
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!API_BASE_URL) {
    throw new ApiRequestError(
      0,
      "api_not_configured",
      "The app isn't connected to its API yet (VITE_API_BASE_URL is missing from this build).",
      ""
    );
  }
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...init.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiRequestError(
      response.status,
      body?.error.code ?? "unknown_error",
      body?.error.message ?? `Request failed with status ${response.status}`,
      body?.error.requestId ?? response.headers.get("X-Request-Id") ?? ""
    );
  }

  return response.json() as Promise<T>;
}
